import type { ProductListingQuery } from '@akademi/site-builder-runtime';
import { DEFAULT_LISTING_PREFIX, type ListingPrefix } from './listingPrefix';

/**
 * The listing query, in three representations: what a block asks for, what the
 * backend's `/catalogue/listing` takes, and what a facet click puts in the URL.
 *
 * <p>They are deliberately the same shape read three ways, so a facetted URL
 * and a block-issued call cannot drift apart and produce two pages of the same
 * results at two URLs.
 *
 * <p>`minPrice` / `maxPrice` stay decimal STRINGS all the way to the query
 * string, for the same reason `MoneyValue.amount` does.
 */

/** Repeated `attr` entries are `code:value`, matching `parseAttributes` on the
 *  backend. One entry per selected value, so multi-select works. */
function attributeEntries(attributes: Record<string, string[]> | undefined): string[] {
  if (!attributes) return [];
  const out: string[] = [];
  for (const [code, values] of Object.entries(attributes)) {
    for (const value of values ?? []) {
      if (code && value) out.push(`${code}:${value}`);
    }
  }
  return out;
}

/** Query parameters for `GET /public/storefront/catalogue/listing`.
 *
 *  <p>Note what is NOT here: a tenant id. It never crosses this boundary — the
 *  backend resolves tenant and channel from the request host, and `domain` is
 *  added server-side by the fetch layer, not by a caller. */
export function listingQueryToParams(query: ProductListingQuery = {}): URLSearchParams {
  const params = new URLSearchParams();
  if (query.categorySlugPath) params.set('category', query.categorySlugPath);
  if (query.brandSlug) params.set('brand', query.brandSlug);
  if (query.q) params.set('q', query.q);
  if (query.sort) params.set('sort', query.sort);
  if (typeof query.page === 'number' && query.page > 0) params.set('page', String(query.page));
  if (typeof query.size === 'number' && query.size > 0) params.set('size', String(query.size));
  if (query.inStockOnly) params.set('inStock', 'true');
  if (query.minPrice) params.set('minPrice', query.minPrice);
  if (query.maxPrice) params.set('maxPrice', query.maxPrice);
  if (query.featured) params.set('featured', 'true');
  if (query.productType) params.set('productType', query.productType);
  for (const entry of attributeEntries(query.attributes)) params.append('attr', entry);
  return params;
}

/**
 * Read a listing query back out of a URL's search params.
 *
 * <p>`categorySlugPath` deliberately is NOT read from the query string: a
 * category listing's category comes from its path (`/c/solar/inverters`), and
 * accepting it from `?category=` too would give one listing two URLs.
 */
export function paramsToListingQuery(
  params: URLSearchParams | Record<string, string | string[] | undefined>,
): ProductListingQuery {
  const search =
    params instanceof URLSearchParams ? params : recordToSearchParams(params);

  const query: ProductListingQuery = {};
  const brand = search.get('brand');
  if (brand) query.brandSlug = brand;
  const q = search.get('q');
  if (q) query.q = q;
  const sort = search.get('sort');
  if (sort) query.sort = sort;
  const page = Number(search.get('page'));
  if (Number.isInteger(page) && page > 0) query.page = page;
  const size = Number(search.get('size'));
  if (Number.isInteger(size) && size > 0) query.size = size;
  if (search.get('inStock') === 'true') query.inStockOnly = true;
  const minPrice = search.get('minPrice');
  if (minPrice && /^\d+(\.\d+)?$/.test(minPrice)) query.minPrice = minPrice;
  const maxPrice = search.get('maxPrice');
  if (maxPrice && /^\d+(\.\d+)?$/.test(maxPrice)) query.maxPrice = maxPrice;
  if (search.get('featured') === 'true') query.featured = true;
  const productType = search.get('productType');
  if (productType) query.productType = productType;

  const attributes: Record<string, string[]> = {};
  for (const entry of search.getAll('attr')) {
    const colon = entry.indexOf(':');
    if (colon <= 0 || colon === entry.length - 1) continue;
    const code = entry.slice(0, colon);
    const value = entry.slice(colon + 1);
    (attributes[code] ??= []).push(value);
  }
  if (Object.keys(attributes).length) query.attributes = attributes;
  return query;
}

function recordToSearchParams(
  record: Record<string, string | string[] | undefined>,
): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(record)) {
    if (value == null) continue;
    for (const one of Array.isArray(value) ? value : [value]) params.append(key, one);
  }
  return params;
}

/**
 * The canonical URL for a listing state, built from the category's public path
 * and nothing else.
 *
 * <p>Facet and page state is a query string on the SAME path, never a new path
 * segment: `/c/solar/inverters?brand=x` is one page's filtered view, and
 * `/c/solar/inverters/brand/x` would be a second URL for it.
 *
 * <p>`prefix` is the shop's own listing word (`listingPrefixFor(site)`), and it
 * defaults to `c` — the word every shop used before it was configurable, so a
 * caller that cannot see the site still builds the URL that is already indexed
 * rather than a 404. A route that renders a listing passes the tenant's, and
 * binds it into the `listingPath` it hands its own controls: a facet link that
 * disagreed with the page it is on would send a shopper through a redirect on
 * every click.
 */
export function listingPath(
  categoryPath: string,
  query: ProductListingQuery = {},
  prefix: ListingPrefix = DEFAULT_LISTING_PREFIX,
): string {
  const base = `/${prefix}/${categoryPath.replace(/^\/+|\/+$/g, '')}`;
  const params = listingQueryToParams({ ...query, categorySlugPath: undefined });
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

/**
 * The URL for a search result state: `/search`, with the text and every filter in the query
 * string. It has the signature of {@link listingPath} so the listing's own controls — filters,
 * sort, pages — can build search URLs; a search spans the whole catalogue, so the category
 * argument is ignored.
 */
export function searchPath(_categoryPath: string, query: ProductListingQuery = {}): string {
  const params = listingQueryToParams({ ...query, categorySlugPath: undefined });
  const qs = params.toString();
  return qs ? `/search?${qs}` : '/search';
}
